import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCubes, faArrowLeft, faEye, faFileAlt, faCheckCircle, faMountain } from '@fortawesome/free-solid-svg-icons';
import { formatDate } from '../../../utils/dateFormatter';
import { getFineAggregateTestReportData } from '../../../apis/fineAggregate';

const FineAggregateTestResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testData, setTestData] = useState({});
  const [testRequestId, setTestRequestId] = useState(null);

  const testConfigurations = {
    bulkDensity: {
      name: 'Bulk Density',
      description: 'Determination of Bulk Density of Fine Aggregate',
      color: 'var(--vitrag-info)'
    },
    sieveAnalysis: {
      name: 'Sieve Analysis',
      description: 'Grading of Fine Aggregate by Sieve Analysis',
      color: 'var(--vitrag-primary)'
    },
    specificGravity: {
      name: 'Specific Gravity & Water Absorption',
      description: 'Determination of Specific Gravity and Water Absorption',
      color: '#9C27B0'
    }
  };

  const testOrderMap = {
    bulkDensity: 1,
    sieveAnalysis: 2,
    specificGravity: 3
  };

  useEffect(() => {
    console.log('🔍 [TEST RESULTS] Location state received:', location.state);
    if (location.state) {
      // Get selectedTests from state
      if (location.state.selectedTests) {
        const sortedTests = [...location.state.selectedTests].sort((a, b) => {
          const orderA = testOrderMap[a] || 999;
          const orderB = testOrderMap[b] || 999;
          return orderA - orderB;
        });
        setSelectedTests(sortedTests);
        console.log('🔍 [TEST RESULTS] Selected tests:', sortedTests);
      }
      
      // Get testRequestId from state
      if (location.state.testRequestId) {
        setTestRequestId(location.state.testRequestId);
        console.log('🔍 [TEST RESULTS] Test Request ID:', location.state.testRequestId);
      }
      
      // Debug: Check formData for sieve analysis
      if (location.state.formData) {
        console.log('🔍 [TEST RESULTS] FormData keys:', Object.keys(location.state.formData));
        if (location.state.selectedTests?.includes('sieveAnalysis')) {
          console.log('🔍 [TEST RESULTS] Sieve Analysis data in formData:', {
            total_weight: location.state.formData.total_weight,
            sieve_weight_retained_01: location.state.formData.sieve_weight_retained_01,
            sieve_weight_retained_02: location.state.formData.sieve_weight_retained_02,
            sieve_percent_weight_retained_01: location.state.formData.sieve_percent_weight_retained_01,
            sieve_cumulative_percent_retained_01: location.state.formData.sieve_cumulative_percent_retained_01,
            sieve_percent_passing_01: location.state.formData.sieve_percent_passing_01,
            fineness_modulus: location.state.formData.fineness_modulus,
            zone_result: location.state.formData.zone_result,
            sieve_sum_cumulative_excluding_pan: location.state.formData.sieve_sum_cumulative_excluding_pan
          });
        }
      }
      
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        if (location.state.selectedTests) {
          const mock = {};
          location.state.selectedTests.forEach((t) => {
            mock[t] = {
              status: 'completed',
              completedDate: new Date().toLocaleDateString(),
              observations: 'Test observations completed successfully'
            };
          });
          setTestData(mock);
        }
      }, 1200);
    } else {
      navigate('/fine-aggregate-form');
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 768px) {
        .test-result-card { margin-bottom: 1rem; }
        .test-result-card .card-body { padding: 1rem; }
        .test-result-card .btn { font-size: 0.875rem; padding: 0.5rem 1rem; }
      }
      .test-result-card { transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out; }
      .test-result-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
      .btn-observation { transition: all 0.2s ease-in-out; }
      .btn-observation:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,123,255,0.3); }
      .btn-report { transition: all 0.2s ease-in-out; }
      .btn-report:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,123,255,0.3); }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const handleViewObservationSheet = (testId) => {
    const formData = location.state?.formData || {};
    const params = new URLSearchParams();
    
    // HTTP-based debug logging
    const logToServer = (message, data) => {
      fetch('http://127.0.0.1:7242/ingest/30abe990-36a0-4ac0-8ff5-37cb3c3d7fe3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'FineAggregateTestResults.js:handleViewObservationSheet',
          message: message,
          data: data,
          timestamp: Date.now(),
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'A'
        })
      }).catch(() => {});
    };
    
    // Debug logging
    console.log('🔍 [OBSERVATION SHEET] Opening observation sheet for test:', testId);
    console.log('🔍 [OBSERVATION SHEET] FormData keys:', Object.keys(formData));
    console.log('🔍 [OBSERVATION SHEET] FormData values count:', Object.keys(formData).length);
    logToServer('Opening observation sheet', { testId, formDataKeys: Object.keys(formData), formDataCount: Object.keys(formData).length });
    
    if (testId === 'sieveAnalysis') {
      const sieveFields = [
        'total_weight',
        'sieve_weight_retained_01', 'sieve_weight_retained_02', 'sieve_weight_retained_03',
        'sieve_weight_retained_04', 'sieve_weight_retained_05', 'sieve_weight_retained_06',
        'sieve_weight_retained_07', 'sieve_weight_retained_08', 'sieve_weight_retained_09',
        'sieve_percent_weight_retained_01', 'sieve_percent_weight_retained_02',
        'sieve_cumulative_percent_retained_01', 'sieve_cumulative_percent_retained_02',
        'sieve_percent_passing_01', 'sieve_percent_passing_02',
        'fineness_modulus', 'zone_result', 'sieve_sum_cumulative_excluding_pan'
      ];
      const sieveData = {};
      sieveFields.forEach(field => {
        sieveData[field] = formData[field];
      });
      console.log('🔍 [OBSERVATION SHEET] Sieve Analysis data check:', sieveData);
      console.log('🔍 [OBSERVATION SHEET] Total weight value:', formData.total_weight, 'Type:', typeof formData.total_weight);
      logToServer('Sieve Analysis data check', sieveData);
    }

    // List of date fields that need formatting to DD/MM/YYYY
    const dateFields = [
      'date_of_receipt',
      'date_of_testing',
      'tested_by_date',
      'checked_by_date',
      'verified_by_date'
    ];

    // Mapping for sieve analysis fields: frontend field names -> HTML element IDs
    const sieveFieldMapping = {
      // Weight Retained: frontend (01-09) -> HTML (10mm, 4.75mm, 2.36mm, 1.18mm, 600mic, 300mic, 150mic, 75mic, pan)
      'sieve_weight_retained_01': 'wt_ret_10mm',
      'sieve_weight_retained_02': 'wt_ret_4.75mm',
      'sieve_weight_retained_03': 'wt_ret_2.36mm',
      'sieve_weight_retained_04': 'wt_ret_1.18mm',
      'sieve_weight_retained_05': 'wt_ret_600mic',
      'sieve_weight_retained_06': 'wt_ret_300mic',
      'sieve_weight_retained_07': 'wt_ret_150mic',  // 150 mic
      'sieve_weight_retained_08': 'wt_ret_75mic',   // 75 mic
      'sieve_weight_retained_09': 'wt_ret_pan',     // Pan
      // % Weight Retained
      'sieve_percent_weight_retained_01': 'pct_ret_10mm',
      'sieve_percent_weight_retained_02': 'pct_ret_4.75mm',
      'sieve_percent_weight_retained_03': 'pct_ret_2.36mm',
      'sieve_percent_weight_retained_04': 'pct_ret_1.18mm',
      'sieve_percent_weight_retained_05': 'pct_ret_600mic',
      'sieve_percent_weight_retained_06': 'pct_ret_300mic',
      'sieve_percent_weight_retained_07': 'pct_ret_150mic',  // 150 mic
      'sieve_percent_weight_retained_08': 'pct_ret_75mic',    // 75 mic
      'sieve_percent_weight_retained_09': 'pct_ret_pan',     // Pan
      // Cumulative % Weight Retained
      'sieve_cumulative_percent_retained_01': 'cum_pct_ret_10mm',
      'sieve_cumulative_percent_retained_02': 'cum_pct_ret_4.75mm',
      'sieve_cumulative_percent_retained_03': 'cum_pct_ret_2.36mm',
      'sieve_cumulative_percent_retained_04': 'cum_pct_ret_1.18mm',
      'sieve_cumulative_percent_retained_05': 'cum_pct_ret_600mic',
      'sieve_cumulative_percent_retained_06': 'cum_pct_ret_300mic',
      'sieve_cumulative_percent_retained_07': 'cum_pct_ret_150mic',  // 150 mic
      'sieve_cumulative_percent_retained_08': 'cum_pct_ret_75mic',    // 75 mic
      'sieve_cumulative_percent_retained_09': 'cum_pct_ret_pan',     // Pan
      // % Passing
      'sieve_percent_passing_01': 'pct_pass_10mm',
      'sieve_percent_passing_02': 'pct_pass_4.75mm',
      'sieve_percent_passing_03': 'pct_pass_2.36mm',
      'sieve_percent_passing_04': 'pct_pass_1.18mm',
      'sieve_percent_passing_05': 'pct_pass_600mic',
      'sieve_percent_passing_06': 'pct_pass_300mic',
      'sieve_percent_passing_07': 'pct_pass_150mic',  // 150 mic
      'sieve_percent_passing_08': 'pct_pass_75mic',   // 75 mic
      'sieve_percent_passing_09': 'pct_pass_pan',     // Pan
      // Calculated fields
      'sieve_sum_cumulative_excluding_pan': 'sum_cumulative',
      'fineness_modulus': 'fineness_modulus',
      'zone_result': 'zone_result',
      'total_weight': 'total_weight'
    };

    // Add all formData keys to params with date formatting and field mapping
    Object.keys(formData).forEach(key => {
      const value = formData[key];
      // Handle null, undefined, empty string - but allow 0 as valid value
      if (value !== undefined && value !== null && value !== '') {
        // Format dates to DD/MM/YYYY before adding to params
        if (dateFields.includes(key) && value) {
          params.append(key, formatDate(value));
        } else {
          // For sieve analysis fields, map to HTML element IDs
          if (testId === 'sieveAnalysis' && sieveFieldMapping[key]) {
            const mappedKey = sieveFieldMapping[key];
            params.append(mappedKey, String(value));
            // Also keep original key for backward compatibility
            params.append(key, String(value));
            console.log(`✅ [OBSERVATION SHEET] Mapped ${key} -> ${mappedKey}: ${value}`);
            logToServer('Field mapped', { originalKey: key, mappedKey: mappedKey, value: value });
          } else {
            params.append(key, String(value));
          }
        }
      } else if (value === 0 || value === '0') {
        // Allow zero values (they are valid for numeric fields)
        if (testId === 'sieveAnalysis' && sieveFieldMapping[key]) {
          const mappedKey = sieveFieldMapping[key];
          params.append(mappedKey, String(value));
          params.append(key, String(value));
        } else {
          params.append(key, String(value));
        }
      }
    });

    const observationSheetPaths = {
      bulkDensity: '/FineAggregate/BulkDensity.html',
      sieveAnalysis: '/FineAggregate/SieveAnalysis.html',
      specificGravity: '/FineAggregate/sg&wa.html'
    };
    
    const path = observationSheetPaths[testId];
    if (path) {
      const url = `${path}?${params.toString()}`;
      console.log('🔍 [OBSERVATION SHEET] Opening URL:', url);
      console.log('🔍 [OBSERVATION SHEET] Params count:', params.toString().split('&').length);
      if (testId === 'sieveAnalysis') {
        const sieveParams = Array.from(params.entries()).filter(([k]) => k.includes('sieve') || k.includes('wt_ret') || k.includes('pct_') || k.includes('cum_') || k === 'total_weight' || k === 'fineness_modulus' || k === 'zone_result' || k === 'sum_cumulative');
        console.log('🔍 [OBSERVATION SHEET] Sieve-specific params:', sieveParams);
        logToServer('Sieve params being sent', { params: sieveParams, totalParams: params.toString().split('&').length });
      }
      window.open(url, '_blank');
    } else {
      alert(`Observation sheet for ${testConfigurations[testId]?.name} will be available in the final report.`);
    }
  };

  const handleViewTestReport = async () => {
    const formData = location.state?.formData || {};
    const currentTestRequestId = testRequestId || location.state?.testRequestId;
    
    try {
      // Fetch report data from backend if testRequestId is available
      let reportData = {};
      if (currentTestRequestId) {
        try {
          reportData = await getFineAggregateTestReportData(currentTestRequestId);
          console.log('✅ Fetched report data from backend:', reportData);
        } catch (error) {
          console.warn('⚠️ Could not fetch report data from backend, using formData only:', error);
        }
      }
      
      // Merge backend data with formData (backend data takes precedence)
      const mergedData = { ...formData, ...reportData };
      
      const params = new URLSearchParams();
      
      // List of date fields that need formatting to DD/MM/YYYY
      const dateFields = [
        'date_of_receipt',
        'date_of_testing',
        'date_of_report',
        'date_of_material_receipt',
        'tested_by_date',
        'checked_by_date',
        'verified_by_date'
      ];

      // Add all merged data keys to params with date formatting
      Object.keys(mergedData).forEach(key => {
        if (mergedData[key] !== undefined && mergedData[key] !== null && mergedData[key] !== '') {
          // Format dates to DD/MM/YYYY before adding to params
          if (dateFields.includes(key) && mergedData[key]) {
            params.append(key, formatDate(mergedData[key]));
          } else {
            // For period_of_testing, it should already be formatted, but ensure it's a string
            if (key === 'period_of_testing') {
              params.append(key, String(mergedData[key]));
            } else {
              params.append(key, mergedData[key]);
            }
          }
        }
      });
      
      // Ensure customer_site_address is included (with multiple fallback options)
      if (!params.has('customer_site_address') && !params.has('site_address')) {
        const siteAddress = mergedData.customer_site_address || mergedData.site_address || '';
        if (siteAddress) {
          params.append('customer_site_address', siteAddress);
          params.append('site_address', siteAddress);
        }
      }
      
      // Explicitly ensure reviewer and authorized fields are included with fallbacks
      // Reviewer fields
      const reviewerName = mergedData.reviewer_name || mergedData.reviewed_by || 'Lalita S. Dussa - Quality Manager';
      const reviewerDesignation = mergedData.reviewer_designation || 'Quality Manager';
      const reviewerGraduation = mergedData.reviewer_graduation || '';
      
      // Authorized fields with defaults
      const authorizedName = mergedData.authorized_name || mergedData.authorized_by || 'Mr. Prakarsh A Sangave';
      const authorizedDesignation = mergedData.authorized_designation || 'Chief Executive Officer';
      const authorizedQualification = mergedData.authorized_qualification || 'M.E(Civil-Structures)';
      const authorizedQualification2 = mergedData.authorized_qualification_2 || 'MTech (Civil-Geotechnical), M.I.E, F.I.E.';
      
      // Always add reviewer fields (even if empty, use defaults)
      if (!params.has('reviewer_name')) {
        params.append('reviewer_name', reviewerName);
        console.log(`✅ [REPORT] Added reviewer_name: ${reviewerName}`);
      }
      if (!params.has('reviewer_designation')) {
        params.append('reviewer_designation', reviewerDesignation);
        console.log(`✅ [REPORT] Added reviewer_designation: ${reviewerDesignation}`);
      }
      if (!params.has('reviewer_graduation')) {
        params.append('reviewer_graduation', reviewerGraduation);
        console.log(`✅ [REPORT] Added reviewer_graduation: ${reviewerGraduation}`);
      }
      if (!params.has('reviewed_by')) {
        params.append('reviewed_by', reviewerName);
      }
      
      // Always add authorized fields (even if empty, use defaults)
      if (!params.has('authorized_name')) {
        params.append('authorized_name', authorizedName);
        console.log(`✅ [REPORT] Added authorized_name: ${authorizedName}`);
      }
      if (!params.has('authorized_designation')) {
        params.append('authorized_designation', authorizedDesignation);
        console.log(`✅ [REPORT] Added authorized_designation: ${authorizedDesignation}`);
      }
      if (!params.has('authorized_qualification')) {
        params.append('authorized_qualification', authorizedQualification);
        console.log(`✅ [REPORT] Added authorized_qualification: ${authorizedQualification}`);
      }
      if (!params.has('authorized_qualification_2')) {
        params.append('authorized_qualification_2', authorizedQualification2);
        console.log(`✅ [REPORT] Added authorized_qualification_2: ${authorizedQualification2}`);
      }
      if (!params.has('authorized_by')) {
        params.append('authorized_by', authorizedName);
      }
      
      console.log('📄 Opening report with params:', params.toString());
      console.log('📄 Reviewer fields check:', {
        reviewer_name: params.get('reviewer_name'),
        reviewer_designation: params.get('reviewer_designation'),
        reviewer_graduation: params.get('reviewer_graduation'),
        authorized_name: params.get('authorized_name'),
        authorized_designation: params.get('authorized_designation'),
        authorized_qualification: params.get('authorized_qualification')
      });
      
      // Open the comprehensive test report in a new tab
      window.open(`/FineAggregate/FineAggregateTestReport.html?${params.toString()}`, '_blank');
    } catch (error) {
      console.error('❌ Error opening test report:', error);
      alert('Failed to open test report. Please try again.');
    }
  };

  const handleBackToForm = () => {
    navigate('/fine-aggregate-form');
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#1C2333' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
          <h5 className="text-white">Processing Test Data...</h5>
          <p className="text-muted">Please wait while we prepare your test results</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--vitrag-main-bg)' }}>
      <div className="py-4" style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={8}>
              <div className="d-flex align-items-center flex-wrap">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center me-3 mb-2 mb-md-0"
                  style={{ width: '60px', height: '60px', backgroundColor: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.3)' }}
                >
                  <FontAwesomeIcon icon={faMountain} size="2x" className="text-white" />
                </div>
                <div>
                  <h2 className="mb-1 text-white fw-bold">Fine Aggregate Testing - Results</h2>
                  <p className="mb-0 text-white-50">Test data has been saved successfully</p>
                </div>
              </div>
            </Col>
            <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0"></Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        <Row className="mb-4">
          <Col>
            <Alert variant="success" className="border-0 shadow-sm alert-vitrag" style={{ backgroundColor: 'var(--vitrag-alert-success-bg)', borderColor: 'var(--vitrag-alert-success-border)', color: 'var(--vitrag-alert-success-text)' }}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="me-3" style={{ color: 'var(--vitrag-success)' }} size="lg" />
                <div>
                  <h5 className="mb-1" style={{ color: 'var(--vitrag-alert-success-text)' }}>Test Data Saved Successfully!</h5>
                  <p className="mb-0" style={{ color: 'var(--vitrag-alert-success-text)' }}>All selected tests have been completed and data has been saved to the system.</p>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Card className="border-0 shadow-sm test-result-card card-vitrag">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary-bg)', borderBottom: '1px solid var(--vitrag-card-border)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-primary-text)' }}>
                  <FontAwesomeIcon icon={faCubes} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                  Selected Tests ({selectedTests.length})
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                {selectedTests.map((testId, index) => {
                  const cfg = testConfigurations[testId];
                  if (!cfg) return null;
                  return (
                    <div key={testId} className={`p-4 ${index !== selectedTests.length - 1 ? 'border-bottom' : ''}`} style={{ backgroundColor: index % 2 === 0 ? 'var(--vitrag-card-bg)' : 'var(--vitrag-secondary-bg)' }}>
                      <Row className="align-items-center">
                        <Col xs={12} md={8}>
                          <div className="d-flex align-items-center flex-wrap">
                            <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3 mb-2 mb-md-0" style={{ width: '50px', height: '50px', backgroundColor: cfg.color + '20', border: `2px solid ${cfg.color}` }}>
                              <FontAwesomeIcon icon={faCubes} style={{ color: cfg.color }} />
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold" style={{ color: 'var(--vitrag-primary-text)' }}>{cfg.name}</h6>
                              <p className="mb-0 small" style={{ color: 'var(--vitrag-secondary-text)' }}>{cfg.description}</p>
                              <div className="d-flex align-items-center mt-1 flex-wrap">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" style={{ color: 'var(--vitrag-success)' }} size="sm" />
                                <span className="small fw-medium" style={{ color: 'var(--vitrag-success)' }}>Completed</span>
                                <span className="small ms-2" style={{ color: 'var(--vitrag-secondary-text)' }}>{testData[testId]?.completedDate}</span>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewObservationSheet(testId)}
                            className="d-flex align-items-center w-100 w-md-auto justify-content-center btn-observation"
                            style={{ borderColor: 'var(--vitrag-gold)', color: 'var(--vitrag-gold)', backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--vitrag-gold)'; e.target.style.color = 'var(--vitrag-primary-text)'; }}
                            onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = 'var(--vitrag-gold)'; }}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            View Observation Sheet
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card className="border-0 shadow-sm card-vitrag">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary-bg)', borderBottom: '1px solid var(--vitrag-card-border)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-primary-text)' }}>
                  <FontAwesomeIcon icon={faFileAlt} className="me-2" style={{ color: 'var(--vitrag-info)' }} />
                  Comprehensive Test Report
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col xs={12} md={8}>
                    <h6 className="mb-2 fw-bold" style={{ color: 'var(--vitrag-primary-text)' }}>Generate Complete Test Report</h6>
                    <p className="mb-0" style={{ color: 'var(--vitrag-secondary-text)' }}>View a comprehensive report containing all test results, observations, and analysis for the selected tests.</p>
                  </Col>
                  <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
                    <Button variant="primary" size="lg" onClick={handleViewTestReport} className="d-flex align-items-center w-100 w-md-auto justify-content-center btn-report btn-vitrag-primary">
                      <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                      View Test Report
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col className="text-center">
            <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3">
              <Button
                variant="outline-secondary"
                onClick={handleBackToForm}
                className="d-flex align-items-center"
                style={{ minWidth: '150px', borderColor: 'var(--vitrag-secondary)', color: 'var(--vitrag-secondary)', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--vitrag-secondary)'; e.target.style.color = 'var(--vitrag-primary-text)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = 'var(--vitrag-secondary)'; }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Form
              </Button>
              <Button variant="success" onClick={() => navigate('/other-services-dashboard')} className="d-flex align-items-center btn-vitrag-primary" style={{ minWidth: '150px' }}>
                <FontAwesomeIcon icon={faCubes} className="me-2" />
                Go to Dashboard
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FineAggregateTestResults;
